import { FunctionComponent, SVGProps } from 'react';
import merge from 'lodash/merge';
import styled, { css, CSSProperties } from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { breakpoint, BreakpointProp } from '../../utils/styles/breakpoints';
import { DataTestId } from '../layout/DataTestIdWrapper';

interface IconBaseProps {
    stroke?: BreakpointProp<CSSProperties['stroke']>;
    fill?: BreakpointProp<CSSProperties['fill']>;
    fillOpacity?: BreakpointProp<CSSProperties['fillOpacity']>;
    fontSize?: BreakpointProp<CSSProperties['fontSize']>;
    size?: BreakpointProp<CSSProperties['fontSize']>;
    width?: BreakpointProp<CSSProperties['width']>;
    height?: BreakpointProp<CSSProperties['height']>;
    margin?: BreakpointProp<CSSProperties['margin']>;
    alignSelf?: BreakpointProp<CSSProperties['alignSelf']>;
    dataTestId?: DataTestId;
}

export type IconProps = SVGProps<SVGSVGElement> & IconBaseProps;

const iconsModelStyles = (props: IconBaseProps) =>
    merge(
        breakpoint('fill', props.fill ?? 'currentColor'),
        breakpoint('fillOpacity', props.fillOpacity),
        breakpoint('stroke', props.stroke),
        breakpoint('fontSize', props.size || props.fontSize),
        breakpoint('width', props.width),
        breakpoint('height', props.height),
        breakpoint('margin', props.margin),
        breakpoint('alignSelf', props.alignSelf),
    );

export function createIconComponent(
    Component: FunctionComponent<SVGProps<SVGSVGElement>>,
): FunctionComponent<IconProps> {
    return styled(Component)
        .attrs<IconProps, IconProps>(({ dataTestId, ...props }) => ({
            ...props,
            'data-test-id': dataTestId,
        }))
        .withConfig({ shouldForwardProp: shouldIgnoreProp('dataTestId') })<IconBaseProps>`
        display: block;
        user-select: none;
        width: 1em;
        height: 1em;
        flex-shrink: 0;
        transition-property: fill, stroke;
        transition-duration: 0.15s;
        transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);

        ${css<IconBaseProps>(iconsModelStyles)}
    `;
}
